// Smooth scrolling for ws4kp's long lists (Hourly Forecast, Travel Forecast, Hazards).
// Stock ws4kp moves the list in whole pixels on a 40 ms timer: 25 steps a second against a 60 Hz screen, and
// steps that alternate 2 px and 1 px because the per-step distance is fractional. Both read as a hitch, worse
// once kiosk scaling multiplies every step. Here the timer still decides where the list should be (so skip,
// back and pause behave as before) and an animation frame glides between its ticks with sub-pixel offsets.
// No upstream file changes: this replaces one method on the shared base class.

import ScrollWeatherDisplay from '../scroll-weather-display.mjs';
import settings from '../settings.mjs';

const state = new WeakMap();

const offsetAt = (timing, count) => Math.max(0, Math.min(
	timing.maxOffset,
	(count - timing.initialCounts) * timing.pixelsPerCount,
));

// Snapped to whole screen pixels (Oct 26, Jared: Travel's names "jumping up and down"): a sub-pixel offset puts the
// pixel font's rows on different screen rows from frame to frame, so letters shimmer. The frame is scaled (2.25x on
// a 1080p TV), so one CSS pixel is several screen pixels and snapping still moves every frame.
const screenScale = (lines) => {
	const css = lines.offsetHeight;
	const shown = lines.getBoundingClientRect().height;
	return (css > 0 && shown > 0 ? shown / css : 1) * (window.devicePixelRatio || 1);
};
const render = (s, count) => {
	const k = s.scale || 1;
	const y = Math.round(offsetAt(s.timing, count) * k) / k;
	s.lines.style.transform = `translate3d(0, ${(-y).toFixed(3)}px, 0)`;
	// Oct 31 (Jared: a gap between the banner and the column headers, rows showing through as they pass): what has
	// scrolled up behind the header bar isn't drawn at all, so no seam from the 2.25x scaling can let a sliver through
	s.lines.style.clipPath = `inset(${y.toFixed(3)}px 0 0 0)`;
};

const frame = (s) => () => {
	s.drawn = performance.now();
	const elapsed = s.drawn - s.at;
	// lead the last tick by at most one step, so a late tick holds rather than overshoots
	render(s, s.count + Math.min(1, elapsed / s.interval));
	// the timer stops when the screen leaves or the page pauses; stop drawing soon after
	if (elapsed < s.interval * 3) s.raf = requestAnimationFrame(frame(s));
	else s.raf = null;
};

ScrollWeatherDisplay.prototype.baseCountChange = function smoothBaseCountChange(count) {
	const lines = this.elem.querySelector(this.linesSelector);
	if (!lines || !this.scrollTiming) return;

	let s = state.get(this);
	if (!s) {
		s = { count: 0 };
		state.set(this, s);
	}
	// new content, a reset to the top, or a skip: snap instead of gliding
	const jump = s.lines !== lines || s.timing !== this.scrollTiming || count < s.count || count > s.count + 2;
	s.lines = lines;
	s.timing = this.scrollTiming;
	s.count = count;
	s.at = performance.now();
	s.interval = (this.timing.baseDelay || 40) * (settings.speed?.value ?? 1);
	if (jump || !s.scale) s.scale = screenScale(lines);

	lines.style.willChange = 'transform';
	// a throttled window (covered, battery saver) may draw frames seconds apart: keep the stock step pace then
	const starved = s.raf && s.at - (s.drawn ?? 0) > s.interval * 3;
	if (jump || starved) render(s, count);
	if (!s.raf) s.raf = requestAnimationFrame(frame(s));
};
